/**
 * Settings → Tax & VAT — per-tenant tax configuration (new tab).
 *
 * Tax is computed SERVER-SIDE on the amount at checkout (never the FE) — this
 * screen reads + edits the config: prices-include-tax, the rate table (real
 * add / edit / delete), the tax id on receipts, and the registrations. No
 * baked data — read from `getTaxSettings`; every mutation persists through the
 * mock ops and the screen replaces its state from the server-canonical response.
 */
import { useEffect, useState } from "react";
import { alpha, Btn, Card, Dropdown, Icon, IconTile, InlineEmptyState, Input, MediaRow, Pill, T, Table, Toggle, tx, type TableColumn } from "@v0y4ge/jrni-ui";
import { PageHeader } from "../../shell/chrome";
import { EmptyState, RowActions } from "../../shell/composites";
import SettingsShell from "./SettingsShell";
import ConfirmModal from "../../shell/ConfirmModal";
import { toast } from "../../shell/toast";
import { DataSourceStrip } from "../../shell/opSource";
import { useT } from "../../lib/i18n";
import {
  getTaxSettings, createTaxRate, updateTaxRate, deleteTaxRate, updateTaxConfig,
  type TaxSettings, type TaxRate, type TaxCategory, type TaxRateInput,
} from "./tax-api";

const TAX_OPS = ["getTaxSettings", "createTaxRate", "updateTaxRate", "deleteTaxRate", "updateTaxConfig"];
const CATEGORIES: TaxCategory[] = ["standard", "reduced", "zero", "exempt"];

const catTone = (c: TaxCategory): "primary" | "neutral" | "warn" =>
  c === "standard" ? "primary" : c === "zero" || c === "exempt" ? "neutral" : "warn";

export default function SettingsTax() {
  const t = useT();
  const [data, setData] = useState<TaxSettings | null>(null);
  const [err, setErr] = useState(false);
  const [tick, setTick] = useState(0);
  const [rateEdit, setRateEdit] = useState<TaxRate | "new" | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<TaxRate | null>(null);

  useEffect(() => {
    let live = true;
    setData(null); setErr(false);
    getTaxSettings().then((d) => { if (live) setData(d); }).catch(() => { if (live) setErr(true); });
    return () => { live = false; };
  }, [tick]);

  const catLabel = (c: TaxCategory) => ({
    standard: t("settings.tax.cat.standard", "Standard"),
    reduced: t("settings.tax.cat.reduced", "Reduced"),
    zero: t("settings.tax.cat.zero", "Zero-rated"),
    exempt: t("settings.tax.cat.exempt", "Exempt"),
  }[c]);

  const doDelete = async () => {
    const r = confirmDel;
    if (!r) return;
    setBusyId(r.id);
    try {
      setData(await deleteTaxRate(r.id));
      toast({ tone: "success", title: t("settings.tax.rateDeleted", "Rate deleted") });
      setConfirmDel(null);
    } catch {
      toast({ tone: "error", title: t("settings.tax.saveFailed", "Couldn't save — try again") });
    } finally {
      setBusyId(null);
    }
  };

  // Rates table columns (component-adoption drain — the hand-rolled <table> is
  // now the shared jrni-ui Table; per-row busy dimming, previously a <tr>
  // style, is reproduced per-cell since Table has no row-style hook).
  const rowFade = (r: TaxRate) => (busyId === r.id ? 0.5 : 1);
  const rateColumns: TableColumn<TaxRate>[] = [
    { id: "name", header: t("settings.tax.col.name", "Name"), cell: (r) => (
      <span style={{ fontWeight: 600, color: T.n1, opacity: rowFade(r) }}>
        {r.name}{r.isDefault ? <Pill tone="primary" style={{ marginLeft: T.space[2] }}>{t("settings.tax.default", "Default")}</Pill> : null}
      </span>
    ) },
    { id: "region", header: t("settings.tax.col.region", "Region"), cell: (r) => <span style={{ color: T.n2, opacity: rowFade(r) }}>{r.region}</span> },
    { id: "category", header: t("settings.tax.col.category", "Category"), cell: (r) => <span style={{ opacity: rowFade(r) }}><Pill tone={catTone(r.category)}>{catLabel(r.category)}</Pill></span> },
    { id: "rate", header: t("settings.tax.col.rate", "Rate"), align: "right", cell: (r) => <span style={{ fontWeight: 700, color: T.n1, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", opacity: rowFade(r) }}>{r.ratePct}%</span> },
    { id: "actions", header: "", align: "right", cell: (r) => (
      <span style={{ display: "inline-flex", opacity: rowFade(r) }}>
        <RowActions
          align="right"
          onEdit={() => setRateEdit(r)}
          editLabel={t("common.actions.edit", "Edit")}
          moreHeader={r.name}
          moreItems={[
            { icon: <Icon.trash size={14} />, label: t("common.actions.delete", "Delete"), danger: true, onClick: () => setConfirmDel(r) },
          ]}
        />
      </span>
    ) },
  ];

  return (
    <SettingsShell activeTab="tax">
      <PageHeader
        title={t("settings.tax.title", "Tax & VAT")}
        subtitle={t("settings.tax.subtitle", "How tax is calculated and shown on your tickets, appointments and receipts.")}
        actions={<Btn size="sm" icon={<Icon.plus size={13} />} onClick={() => setRateEdit("new")}>{t("settings.tax.addRate", "Add rate")}</Btn>}
      />
      <DataSourceStrip screen="tax" ops={TAX_OPS} />

      <div style={{ flex: 1, overflow: "auto", padding: `${T.space[4]} ${T.space[6]} ${T.space[6]}`, display: "flex", flexDirection: "column", gap: T.space[4] }}>
        {err ? (
          <EmptyState
            variant="card"
            tone="danger"
            icon={<Icon.warn size={20} />}
            title={t("settings.tax.loadErrorTitle", "Couldn't load tax settings")}
            body={t("settings.tax.loadError", "Something went wrong loading your tax configuration. Please try again.")}
            action={<Btn variant="secondary" size="sm" icon={<Icon.refresh size={13} />} onClick={() => setTick((n) => n + 1)}>{t("common.actions.tryAgain", "Try again")}</Btn>}
          />
        ) : !data ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: T.space[3] }}>{[0, 1, 2].map((i) => <Card key={i} style={{ height: 88, opacity: 0.55 }} />)}</div>
        ) : (
          <>
            {/* Config summary (editable) */}
            <Card style={{ padding: 0 }}>
              {/* ui-raw: same PanelHeader horizontal-padding mismatch as other
                 settings screens (PanelHeader only sets its own divider
                 paddingBottom, not this bar's uniform 12/16px); kept raw. */}
              <div style={{ padding: `${T.space[3]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: T.space[2] }}>
                <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
                  <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.tax.configuration", "Configuration")}</h3>
                  {data.enabled ? <Pill tone="success" dot>{t("settings.tax.collecting", "Collecting tax")}</Pill> : <Pill tone="neutral">{t("settings.tax.notCollecting", "Not collecting")}</Pill>}
                </div>
                <Btn variant="ghost" size="sm" icon={<Icon.edit size={12} />} onClick={() => setConfigOpen(true)}>{t("common.actions.edit", "Edit")}</Btn>
              </div>
              <div style={{ padding: `${T.space[4]} ${T.space[4]}`, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: T.space[4] }}>
                <Field label={t("settings.tax.pricing", "Price display")} value={data.pricesIncludeTax ? t("settings.tax.inclusive", "Tax-inclusive") : t("settings.tax.exclusive", "Tax added at checkout")} help={data.pricesIncludeTax ? t("settings.tax.inclusiveHelp", "Ticket prices already include tax.") : t("settings.tax.exclusiveHelp", "Tax is added to the price at checkout.")} />
                <Field label={t("settings.tax.engine", "Rate source")} value={data.engine === "stripe_tax" ? "Stripe Tax" : data.engine === "manual" ? t("settings.tax.manualRates", "Your own rates") : "—"} help={t("settings.tax.engineHelp", "Where the tax rate is determined.")} />
                <Field label={t("settings.tax.taxId", "Tax ID on receipts")} value={data.taxId || "—"} mono help={t("settings.tax.taxIdHelp", "Shown on receipts / invoices (VATIN / GSTIN).")} />
              </div>
            </Card>

            {/* How tax works */}
            {/* ui-raw: Banner's compact/default body-text color+line-height are
               fixed inside the component (no style override reaches them) and
               don't match this callout's T.n2/1.55 sizing — kept raw for exact
               parity. */}
            <Card style={{ padding: `${T.space[4]} ${T.space[4]}`, background: T.p4, borderColor: alpha(T.p1, "22"), display: "flex", gap: T.space[2], alignItems: "flex-start" }}>
              <span style={{ color: T.p1, flexShrink: 0, marginTop: 1 /* optical */ }}><Icon.info size={15} /></span>
              <div style={{ fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.55 }}>{t("settings.tax.howItWorks", "Tax is calculated server-side on the order amount at checkout — the same for events, appointments and walk-ins. Change the pricing model (inclusive vs exclusive) before you start selling, since it changes the total charged and the refund maths.")}</div>
            </Card>

            {/* Rates */}
            <Card style={{ padding: 0 }}>
              {/* ui-raw: same PanelHeader horizontal-padding mismatch as other
                 settings screens (PanelHeader only sets its own divider
                 paddingBottom, not this bar's uniform 12/16px); kept raw. */}
              <div style={{ padding: `${T.space[3]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.tax.rates", "Tax rates")}</h3>
                  <p style={{ ...tx.help, marginTop: T.space[1] }}>{t("settings.tax.ratesHelp", "Assign a rate per ticket type or service. The default applies where none is set.")}</p>
                </div>
                <Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => setRateEdit("new")}>{t("settings.tax.addRate", "Add rate")}</Btn>
              </div>
              {/* `emptyState` is a Table prop — not passing it left an empty rate list
                  rendering a bare header strip with no message, which then collided with
                  the Tax registrations heading below. Its sibling table hand-rolled its
                  own empty <div>; both now use the one DS treatment. */}
              <Table<TaxRate>
                columns={rateColumns}
                rows={data.rates}
                getRowId={(r) => r.id}
                emptyState={<InlineEmptyState
                  size="sm"
                  title={t("settings.tax.noRatesYet", "No tax rates yet.")}
                  description={t("settings.tax.noRatesHelp", "Add a rate to charge tax on a ticket type or service.")}
                />}
                style={{ border: "none", borderRadius: T.radius.none }}
              />
            </Card>

            {/* Registrations */}
            <Card style={{ padding: 0 }}>
              {/* ui-raw: same PanelHeader horizontal-padding mismatch as other
                 settings screens; kept raw. */}
              <div style={{ padding: `${T.space[3]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}` }}>
                <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.tax.registrations", "Tax registrations")}</h3>
                <p style={{ ...tx.help, marginTop: T.space[1] }}>{t("settings.tax.registrationsHelp", "The regions you're registered to collect tax in. You can only charge tax where you're registered.")}</p>
              </div>
              {data.registrations.length === 0 ? (
                <InlineEmptyState size="sm" title={t("settings.tax.noRegistrationsYet", "No tax registrations yet.")} />
              ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {data.registrations.map((rg, i) => (
                    <MediaRow
                      key={i}
                      as="li"
                      style={{ padding: `${T.space[3]} ${T.space[4]}`, borderTop: i ? `1px solid ${T.border}` : "none", borderRadius: T.radius.none }}
                      leading={<IconTile icon={<Icon.loc size={14} />} tone="primary" style={{ width: 30, height: 30, borderRadius: T.radius.md }} />}
                      title={<span style={{ fontSize: T.text.body.fontSize }}>{rg.region}</span>}
                      subtitle={<span style={{ fontFamily: T.fontMono }}>{rg.taxId}</span>}
                      trailing={<Pill tone="success" dot>{t("common.status.active", "Active")}</Pill>}
                    />
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}
      </div>

      {rateEdit ? (
        <RateModal
          rate={rateEdit === "new" ? null : rateEdit}
          catLabel={catLabel}
          onClose={() => setRateEdit(null)}
          onSaved={(next) => { setData(next); setRateEdit(null); }}
        />
      ) : null}
      {configOpen && data ? (
        <ConfigModal settings={data} onClose={() => setConfigOpen(false)} onSaved={(next) => { setData(next); setConfigOpen(false); }} />
      ) : null}
      <ConfirmModal
        open={!!confirmDel}
        danger
        busy={!!busyId}
        title={t("settings.tax.deleteTitle", "Delete tax rate?")}
        message={confirmDel
          ? t("settings.tax.deleteBody", "This removes") + ` “${confirmDel.name}” (${confirmDel.ratePct}%). ` + t("settings.tax.deleteBody2", "Orders already placed keep the tax they were charged.")
          : undefined}
        confirmLabel={t("common.actions.delete", "Delete")}
        onConfirm={doDelete}
        onClose={() => { if (!busyId) setConfirmDel(null); }}
      />
    </SettingsShell>
  );
}

function Field({ label, value, help, mono }: { label: string; value: string; help?: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: T.text.label.fontSize, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.05, color: T.soft }}>{label}</div>
      <div style={{ fontSize: T.text.h3.fontSize, fontWeight: 700, color: T.n1, marginTop: T.space[1], fontFamily: mono ? T.fontMono : T.font }}>{value}</div>
      {help ? <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginTop: T.space[1], lineHeight: 1.45 }}>{help}</div> : null}
    </div>
  );
}

// ui-raw: bespoke lightweight modal (backdrop-click-to-close only, no header
// close-X, tinted footer bar) — jrni-ui's Modal always renders a close button
// and its footer has no background tint, so swapping would add an affordance
// and drop a visual that aren't in this design; kept raw for exact parity.
function Overlay({ title, children, footer, onClose }: { title: string; children: React.ReactNode; footer: React.ReactNode; onClose: () => void }) {
  return (
    <div onClick={onClose} role="dialog" aria-label={title} style={{ position: "fixed", inset: 0, background: "var(--jrni-color-surface-overlay)", zIndex: T.z.overlay, display: "flex", alignItems: "center", justifyContent: "center", padding: T.space[5] }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 460, maxWidth: "94vw", background: T.white, borderRadius: T.radius.lg, boxShadow: T.shadowLg, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: `${T.space[4]} ${T.space[6]}`, borderBottom: `1px solid ${T.border}` }}><h3 style={{ fontSize: T.text.h2.fontSize, fontWeight: 700, color: T.n1, margin: 0 }}>{title}</h3></div>
        <div style={{ padding: `${T.space[4]} ${T.space[6]}`, display: "flex", flexDirection: "column", gap: T.space[4] }}>{children}</div>
        <div style={{ padding: `${T.space[4]} ${T.space[6]}`, borderTop: `1px solid ${T.border}`, background: T.n6, display: "flex", justifyContent: "flex-end", gap: T.space[2] }}>{footer}</div>
      </div>
    </div>
  );
}

function RateModal({ rate, catLabel, onClose, onSaved }: { rate: TaxRate | null; catLabel: (c: TaxCategory) => string; onClose: () => void; onSaved: (s: TaxSettings) => void }) {
  const t = useT();
  const [name, setName] = useState(rate?.name ?? "");
  const [ratePct, setRatePct] = useState(String(rate?.ratePct ?? ""));
  const [region, setRegion] = useState(rate?.region ?? "");
  const [category, setCategory] = useState<TaxCategory>(rate?.category ?? "standard");
  const [isDefault, setIsDefault] = useState(!!rate?.isDefault);
  const [busy, setBusy] = useState(false);
  const valid = name.trim().length > 0 && region.trim().length > 0 && ratePct !== "" && !isNaN(Number(ratePct));

  const save = async () => {
    if (!valid || busy) return;
    setBusy(true);
    const input: TaxRateInput = { name: name.trim(), ratePct: Number(ratePct), region: region.trim(), category, isDefault };
    try {
      const next = rate ? await updateTaxRate(rate.id, input) : await createTaxRate(input);
      toast({ tone: "success", title: rate ? t("settings.tax.rateUpdated", "Rate updated") : t("settings.tax.rateAdded", "Rate added") });
      onSaved(next);
    } catch { toast({ tone: "error", title: t("settings.tax.saveFailed", "Couldn't save — try again") }); setBusy(false); }
  };

  return (
    <Overlay title={rate ? t("settings.tax.editRate", "Edit tax rate") : t("settings.tax.newRate", "New tax rate")} onClose={onClose}
      footer={<><Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn><Btn disabled={!valid || busy} onClick={save}>{busy ? t("common.actions.saving", "Saving…") : t("common.actions.save", "Save")}</Btn></>}>
      <Input label={t("settings.tax.field.name", "Name")} value={name} placeholder={t("settings.tax.placeholder", "e.g. UK VAT (standard)")} onChange={(e) => setName(e.target.value)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: T.space[3] }}>
        <Input label={t("settings.tax.field.rate", "Rate %")} value={ratePct} type="number" placeholder="20" onChange={(e) => setRatePct(e.target.value)} />
        <Input label={t("settings.tax.field.region", "Region")} value={region} placeholder={t("settings.tax.placeholder2", "United Kingdom")} onChange={(e) => setRegion(e.target.value)} />
      </div>
      <div>
        <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.n1, marginBottom: T.space[1] }}>{t("settings.tax.field.category", "Category")}</div>
        <Dropdown portal options={CATEGORIES.map((c) => ({ value: c, label: catLabel(c) }))} value={category} onChange={(v) => setCategory(v as TaxCategory)} aria-label={t("settings.tax.field.category", "Category")} style={{ width: "100%" }} />
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: T.space[2], fontSize: T.text.body.fontSize, color: T.n1, cursor: "pointer" }}>
        <Toggle on={isDefault} onChange={() => setIsDefault((v) => !v)} />
        {t("settings.tax.field.default", "Use as the default rate")}
      </label>
    </Overlay>
  );
}

function ConfigModal({ settings, onClose, onSaved }: { settings: TaxSettings; onClose: () => void; onSaved: (s: TaxSettings) => void }) {
  const t = useT();
  const [inclusive, setInclusive] = useState(settings.pricesIncludeTax);
  const [taxId, setTaxId] = useState(settings.taxId ?? "");
  const [enabled, setEnabled] = useState(settings.enabled);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (busy) return; setBusy(true);
    try { onSaved(await updateTaxConfig({ pricesIncludeTax: inclusive, taxId: taxId.trim() || null, enabled })); toast({ tone: "success", title: t("settings.tax.configSaved", "Tax settings saved") }); }
    catch { toast({ tone: "error", title: t("settings.tax.saveFailed", "Couldn't save — try again") }); setBusy(false); }
  };
  return (
    <Overlay title={t("settings.tax.editConfig", "Tax configuration")} onClose={onClose}
      footer={<><Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn><Btn disabled={busy} onClick={save}>{busy ? t("common.actions.saving", "Saving…") : t("common.actions.save", "Save")}</Btn></>}>
      <label style={{ display: "flex", alignItems: "center", gap: T.space[2], fontSize: T.text.body.fontSize, color: T.n1, cursor: "pointer" }}>
        <Toggle on={enabled} onChange={() => setEnabled((v) => !v)} />
        {t("settings.tax.collectTax", "Collect tax on orders")}
      </label>
      <div>
        <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.n1, marginBottom: T.space[1] }}>{t("settings.tax.pricing", "Price display")}</div>
        <Dropdown portal options={[{ value: "inc", label: t("settings.tax.inclusive", "Tax-inclusive") }, { value: "exc", label: t("settings.tax.exclusive", "Tax added at checkout") }]} value={inclusive ? "inc" : "exc"} onChange={(v) => setInclusive(v === "inc")} aria-label={t("settings.tax.pricing", "Price display")} style={{ width: "100%" }} />
      </div>
      <Input label={t("settings.tax.taxId", "Tax ID on receipts")} value={taxId} placeholder={"GB123456789" /* ui-config — sample tax-id format */} onChange={(e) => setTaxId(e.target.value)} />
    </Overlay>
  );
}
